"use client";
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, } from "react";
const NOOP = { emit: () => { } };
const TelemetryContext = createContext(NOOP);
export function useTelemetry() {
    return useContext(TelemetryContext);
}
/**
 * Convenience hook: emit a single `${name}.mount` event the first time
 * a component mounts. Pass a stable `props` object (or memoize at the
 * call site) — the hook intentionally ignores prop changes after mount.
 */
export function useTelemetryMount(name, props) {
    const { emit } = useTelemetry();
    useEffect(() => {
        if (typeof window === "undefined")
            return undefined;
        let fired = false;
        const handle = window.requestAnimationFrame(() => {
            if (fired)
                return;
            fired = true;
            emit(`${name}.mount`, props);
        });
        return () => window.cancelAnimationFrame(handle);
        // Mount-once: deliberately omit prop deps.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}
export function TelemetryProvider({ children, sink, debug = false }) {
    const emit = useCallback((name, props) => {
        const event = { name, props, ts: Date.now() };
        try {
            sink(event);
        }
        catch (error) {
            if (debug)
                console.error("[helm-telemetry] sink threw:", error);
        }
        if (debug)
            console.debug("[helm-telemetry]", event);
    }, [sink, debug]);
    const value = useMemo(() => ({ emit }), [emit]);
    return _jsx(TelemetryContext.Provider, { value: value, children: children });
}
